import React from 'react';
import { Box, Typography, Paper, alpha } from '@mui/material';
import { Autorenew } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Simplified color helper
const getRatingColor = (rating) => {
  const colors = {
    'very_strong': '#059669',
    'strong': '#10b981', 
    'above_average': '#3b82f6',
    'below_average': '#f59e0b',
    'weak': '#f97316',
    'very_weak': '#ef4444'
  };
  return colors[rating] || '#64748b';
};

const SectorRotationLayer = ({ sectorsData, getRatingLabel, getTrendLabel, getFlowLabel }) => {
  const { t, i18n } = useTranslation();

  if (!sectorsData || sectorsData.length === 0) {
    return null;
  }

  return (
    <Paper sx={{ 
      mb: 2, 
      p: 2, 
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      border: '1px solid #e5e7eb',
      borderRadius: 2
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: 1.5,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Autorenew sx={{ fontSize: 18, color: 'white' }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
            {t('safeInvest.layer2.title')}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>
            {t('safeInvest.layer2.description')}
          </Typography>
        </Box>
      </Box>

      {/* Sector List - Clean Table Style */}
      <Box sx={{ 
        border: '1px solid #e5e7eb',
        borderRadius: 1.5,
        overflow: 'hidden'
      }}>
        {/* Header Row */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: '40px 1fr 70px 70px 70px 80px',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: '#f8fafc',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
            #
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
            {t('safeInvest.layer2.sector')}
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', textAlign: 'center' }}>
            {t('safeInvest.layer2.score')}
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', textAlign: 'center' }}>
            RS
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', textAlign: 'center' }}>
            Trend
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', textAlign: 'center' }}>
            Flow
          </Typography>
        </Box>

        {/* Data Rows */}
        {sectorsData.map((sector, idx) => {
          const sectorName = i18n.language === 'vi' 
            ? (sector.sector_name?.vi || sector.sector_name?.en || sector.sector)
            : (sector.sector_name?.en || sector.sector);
          
          const isTop3 = idx < 3;
          const rankColors = ['#f59e0b', '#94a3b8', '#cd7f32'];
          
          return (
            <Box 
              key={sector.sector}
              sx={{ 
                display: 'grid',
                gridTemplateColumns: '40px 1fr 70px 70px 70px 80px',
                gap: 1,
                px: 1.5,
                py: 1,
                alignItems: 'center',
                borderBottom: idx < sectorsData.length - 1 ? '1px solid #f1f5f9' : 'none',
                bgcolor: isTop3 ? alpha('#667eea', 0.03) : 'transparent',
                transition: 'background-color 0.15s',
                '&:hover': {
                  bgcolor: alpha('#667eea', 0.06)
                }
              }}
            >
              {/* Rank */}
              <Typography sx={{ 
                fontSize: '0.8rem', 
                fontWeight: isTop3 ? 800 : 600,
                color: isTop3 ? rankColors[idx] : '#64748b'
              }}>
                {idx + 1}
              </Typography>
              
              {/* Sector Name */}
              <Typography sx={{ 
                fontSize: '0.8rem', 
                fontWeight: isTop3 ? 700 : 500,
                color: '#1e293b'
              }}>
                {sectorName}
              </Typography>
              
              {/* Score */}
              <Typography sx={{ 
                fontSize: '0.85rem', 
                fontWeight: 800,
                color: '#1e293b',
                fontFamily: 'monospace',
                textAlign: 'center'
              }}>
                {sector.score?.toFixed(1)}
              </Typography>
              
              {/* RS */}
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{
                  display: 'inline-block',
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.75,
                  bgcolor: alpha(getRatingColor(sector.rs_rating), 0.1)
                }}>
                  <Typography sx={{ 
                    fontSize: '0.65rem', 
                    fontWeight: 700,
                    color: getRatingColor(sector.rs_rating)
                  }}>
                    {getRatingLabel(sector.rs_rating)}
                  </Typography>
                </Box>
              </Box>
              
              {/* Trend */}
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{
                  display: 'inline-block',
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.75,
                  bgcolor: alpha(getRatingColor(sector.trend_rating), 0.1)
                }}>
                  <Typography sx={{ 
                    fontSize: '0.65rem', 
                    fontWeight: 700,
                    color: getRatingColor(sector.trend_rating)
                  }}>
                    {getRatingLabel(sector.trend_rating)}
                  </Typography>
                </Box>
              </Box>
              
              {/* Flow */}
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{
                  display: 'inline-block',
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.75,
                  bgcolor: alpha(getRatingColor(sector.flow_rating), 0.1)
                }}>
                  <Typography sx={{ 
                    fontSize: '0.65rem', 
                    fontWeight: 700,
                    color: getRatingColor(sector.flow_rating)
                  }}>
                    {getRatingLabel(sector.flow_rating)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default SectorRotationLayer;
